import {
  Autocomplete,
  Box,
  Checkbox,
  Grid,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import React, { useEffect, useState, useMemo } from "react";
import {
  BackButton,
  Button,
  ErrorMessage,
  Heading,
  Input,
  Select,
  UploadImage,
} from "../../components/Common";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useAddNewEmployeeMutation,
  useGetEmployeeDetailQuery,
  useUpdateEmployeeMutation,
  useGetPincodesBySubAdminQuery,
} from "../../services/Employee";
import { bloodGroupList, countryList, userTypeList } from "../../helper";
import { useMessage } from "../../components/Common/Message/MessageContext";
import { useGetserviceDepartmentDropdownQuery } from "../../services/ServiceDepartment";
import {
  useGetCityListQuery,
  useGetStateListQuery,
} from "../../services/Location";
import {
  useGetPincodeByCityQuery,
  useGetExtraPincodeByCityQuery,
} from "../../services/SubAdmin";
import { getAllData } from "../../helper/indexDB";
import {
  useGetAdminListForDropdownQuery,
  useGetSubAdminByAdminQuery,
} from "../../services/Admin";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Maximum file size in bytes (5MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const AddEmployees = () => {
  const { showMessage } = useMessage();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState([]);
  const [userType, setUserType] = useState("");
  const [user, setUser] = useState("");
  const [country, setCountry] = useState(1);
  const [states, setStates] = useState(5);
  const [city, setCity] = useState("");
  const [aadharImage, setAadharImage] = useState([]);
  const [bloodGroup, setBloodGroup] = useState("");
  const [pincodes, setPincodes] = useState([]);
  const [adminRole, setAdminRole] = useState("");
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [selectedSubAdmin, setSelectedSubAdmin] = useState("");
  const [adminList, setAdminList] = useState([]);
  const [subAdminList, setSubAdminList] = useState([]);
  const [userAdminId, setUserAdminId] = useState(""); // Store admin ID for sub-admin
  const [subAdminPincodes, setSubAdminPincodes] = useState([]); // NEW
  const [isSubAdminSelected, setIsSubAdminSelected] = useState(false); // NEW
  const { data: subAdminPincodesData, isLoading: subAdminPincodesLoading } =
    useGetPincodesBySubAdminQuery(selectedSubAdmin, {
      skip: !selectedSubAdmin || !isSubAdminSelected,
    });

  const [formValues, setFormValues] = useState({
    name: "",
    mobile: "",
    email: "",
    guardianName: "",
    guardianMobile: "",
    address: "",
    panNo: "",
    dob: "",
  });
  const [orderDialog, setOrderDialog] = useState({
    open: false,
    pincode: '',
    orders: [],
    totalOrders: 0,
  });

  const [errorMessages, setErrorMessages] = useState({});
  const [profileErrorMessage, setProfileErrorMessage] = useState("");
  const [addharErrorMessage, setAddharErrorMessage] = useState("");

  // RTK Query hooks
  const { data, error, isLoading } = useGetserviceDepartmentDropdownQuery();
  const serviceList = data?.data;

  const {
    data: cityLst,
    error: cityError,
    isLoading: cityLoading,
  } = useGetCityListQuery(states, { skip: !states });
  const cityList = cityLst?.data;

  const {
    data: stateLst,
    error: stateError,
    isLoading: stateLoading,
  } = useGetStateListQuery();
  const stateList = stateLst?.data;

  const { data: extraPincodeData, isLoading: extraPincodeLoading } =
    useGetExtraPincodeByCityQuery(city, {
      skip: !city || isSubAdminSelected, // ADD: || isSubAdminSelected
      refetchOnMountOrArgChange: true,
    });

  const { data: cityPincodeOwnershipData, isLoading: cityPincodeLoading } =
    useGetPincodeByCityQuery(city, {
      skip: !city || isSubAdminSelected, // ADD: || isSubAdminSelected
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    });

  const handleCloseOrderDialog = () => {
    // Re-add the blocked pincode back to selected pincodes
    if (orderDialog.pincode && !pincodes.includes(orderDialog.pincode)) {
      setPincodes(prev => [...prev, orderDialog.pincode]);
    }

    setOrderDialog({
      open: false,
      pincode: '',
      orders: [],
      totalOrders: 0,
    });
  };

  // NEW: Admin and SubAdmin RTK Query hooks
  const { data: adminDropdownData, isLoading: adminDropdownLoading } =
    useGetAdminListForDropdownQuery();

  const { data: subAdminData, isLoading: subAdminLoading } =
    useGetSubAdminByAdminQuery(selectedAdmin, {
      skip: !selectedAdmin,
    });

  const pincodeOwnershipMap = useMemo(() => {
    const map = {};
    const list =
      cityPincodeOwnershipData?.data ||
      cityPincodeOwnershipData?.pinCodeData ||
      [];
    list.forEach((item) => {
      const pin = item.pincode || item.pinCode;
      if (pin) {
        map[pin] = item.adminId || null;
      }
    });
    return map;
  }, [cityPincodeOwnershipData]);

  const pincodeList = useMemo(() => {
    // NEW: If sub-admin is selected, use their specific pincodes
    if (isSubAdminSelected && subAdminPincodes.length > 0) {
      return subAdminPincodes.map((pin) => ({
        pincode: pin.pincode,
        adminId: null,
      }));
    }

    // Otherwise, use city-based pincodes (original logic)
    if (!city) return [];
    const extraPins = extraPincodeData?.data || [];
    return extraPins.map((pin) => ({
      pincode: pin.pincode,
      adminId: pincodeOwnershipMap[pin.pincode] ?? null,
    }));
  }, [
    city,
    extraPincodeData,
    pincodeOwnershipMap,
    isSubAdminSelected,
    subAdminPincodes,
  ]);

  const [addEmployee] = useAddNewEmployeeMutation();
  const [updateEmployee] = useUpdateEmployeeMutation();
  const { data: employeeLst } = useGetEmployeeDetailQuery(state?.id, {
    skip: !state,
    refetchOnMountOrArgChange: true,
  });
  const employeeDetails = employeeLst?.data;

  // Initialize user data from IndexedDB
  useEffect(() => {
    const fetchData = async () => {
      const storedData = await getAllData();
      const currentUserId = storedData?.[0]?.value.id;
      const currentUserRole = storedData?.[0]?.value.type;
      const currentUserAdminId = storedData?.[0]?.value.adminId;

      // console.log("🔍 IndexedDB Data:", storedData?.[0]?.value);
      // console.log("🔍 Current User ID:", currentUserId);
      // console.log("🔍 Current User Role:", currentUserRole);
      // console.log("🔍 Current User AdminId:", currentUserAdminId);

      setUser(currentUserId);
      setAdminRole(currentUserRole);

      // For Sub-Admin (type 3), set admin ID and auto-select both dropdowns
      if (currentUserRole === 3 && currentUserAdminId) {
        // console.log(
        //   "✅ Sub-admin detected, setting adminId:",
        //   currentUserAdminId
        // );
        setUserAdminId(currentUserAdminId);
        setSelectedAdmin(currentUserAdminId);
        setSelectedSubAdmin(currentUserId);
        setIsSubAdminSelected(true);
      }

      // Set city and state for non-super admin
      if (currentUserRole !== 1) {
        setCity(storedData?.[0]?.value.city || "");
        setStates(storedData?.[0]?.value.state || "");
      }
    };

    fetchData();
  }, []);

  // Handle admin dropdown data from RTK Query
  useEffect(() => {
    if (adminDropdownData?.status && adminDropdownData.data) {
      let filteredAdmins = adminDropdownData.data;

      // console.log("📋 All Admins:", adminDropdownData.data);

      // If current user is Admin (type 2), filter to show only current admin
      if (adminRole === 2) {
        filteredAdmins = filteredAdmins.filter((admin) => admin.id === user);
        setSelectedAdmin(user);
      }

      // If current user is Sub-Admin (type 3), filter to show only their assigned admin
      if (adminRole === 3 && userAdminId) {
        // console.log("🔍 Filtering for sub-admin, userAdminId:", userAdminId);
        filteredAdmins = filteredAdmins.filter(
          (admin) => admin.id === parseInt(userAdminId, 10)
        );
        // console.log("✅ Filtered Admins for sub-admin:", filteredAdmins);
      }

      setAdminList(filteredAdmins);
    }
  }, [adminDropdownData, adminRole, user, userAdminId]);

  // Handle sub-admin data from RTK Query
  useEffect(() => {
    if (!selectedAdmin) {
      setSubAdminList([]);
      return;
    }

    if (subAdminData?.status && subAdminData.data) {
      // console.log(
      //   "📋 All Sub-Admins for admin",
      //   selectedAdmin,
      //   ":",
      //   subAdminData.data
      // );

      let filteredSubAdmins = subAdminData.data;

      // If current user is SubAdmin (type 3), filter to show only themselves
      if (adminRole === 3) {
        // console.log("🔍 Filtering for current sub-admin, user ID:", user);
        filteredSubAdmins = filteredSubAdmins.filter((sa) => sa.id === user);
        // console.log("✅ Filtered Sub-Admins:", filteredSubAdmins);
      }

      setSubAdminList(filteredSubAdmins);
    }
  }, [subAdminData, selectedAdmin, adminRole, user]);

  useEffect(() => {
    if (employeeDetails && state) {
      setFormValues({
        name: employeeDetails?.name || "",
        mobile: employeeDetails?.mobile || "",
        email: employeeDetails?.email || "",
        guardianName: employeeDetails?.guardianName || "",
        guardianMobile: employeeDetails?.guardianMobile || "",
        address: employeeDetails?.address || "",
        panNo: employeeDetails?.panNo || "",
        dob: employeeDetails?.dob
          ? new Date(employeeDetails.dob).toISOString().split("T")[0]
          : "",
        id: state.id,
      });
      setProfileImage(employeeDetails?.profileImage || []);
      setAadharImage(employeeDetails?.aadharImage || []);
      setBloodGroup(employeeDetails?.bloodGroup || "");
      setStates(employeeDetails?.state || "");
      setCity(employeeDetails?.city || "");
      setUserType(employeeDetails?.workType?.id || "");
      setPincodes(employeeDetails?.pinCode || []);

      // Only override admin/subadmin if not a sub-admin user
      // For sub-admin, keep the values set from IndexedDB
      if (adminRole !== 3) {
        setSelectedAdmin(employeeDetails?.adminId || "");
        setSelectedSubAdmin(employeeDetails?.subAdminId || "");
        if (employeeDetails?.subAdminId) {
          setIsSubAdminSelected(true);
        }
      }
    }
  }, [employeeDetails, state, adminRole]);

  // NEW: Fetch and handle sub-admin pincodes
  useEffect(() => {
    if (!selectedSubAdmin || !isSubAdminSelected) {
      setSubAdminPincodes([]);
      return;
    }

    // Use data from RTK Query
    if (subAdminPincodesData?.status && subAdminPincodesData.data) {
      setSubAdminPincodes(subAdminPincodesData.data);

      // If editing an employee, filter their pincodes to match sub-admin's pincodes
      if (state?.id && employeeDetails?.pinCode) {
        const subAdminPincodesSet = new Set(
          subAdminPincodesData.data.map((p) => p.pincode)
        );
        const validPincodes = employeeDetails.pinCode.filter((pin) =>
          subAdminPincodesSet.has(pin)
        );
        setPincodes(validPincodes);
      } else {
        setPincodes([]); // Reset pincodes when changing sub-admin
      }
    }
  }, [
    selectedSubAdmin,
    isSubAdminSelected,
    subAdminPincodesData,
    state?.id,
    employeeDetails?.pinCode,
  ]);

  // Add this useEffect for debugging
  useEffect(() => {
    console.log("🔍 Debug Info:");
    console.log("  isSubAdminSelected:", isSubAdminSelected);
    console.log("  selectedSubAdmin:", selectedSubAdmin);
    console.log("  subAdminPincodes:", subAdminPincodes);
    console.log("  subAdminPincodesData:", subAdminPincodesData);
    console.log("  pincodeList:", pincodeList);
  }, [
    isSubAdminSelected,
    selectedSubAdmin,
    subAdminPincodes,
    subAdminPincodesData,
    pincodeList,
  ]);

  useEffect(() => {
    if (!city || !pincodeList.length) {
      if (!isSubAdminSelected) {
        // ADD: Only reset if not using sub-admin pincodes
        setPincodes([]);
      }
      return;
    }

    if (state?.id && employeeDetails?.pinCode && !isSubAdminSelected) {
      // ADD: && !isSubAdminSelected
      const validPincodes = employeeDetails.pinCode.filter((pin) =>
        pincodeList.some((cp) => cp.pincode === pin)
      );
      setPincodes(validPincodes);
    } else if (!isSubAdminSelected) {
      // ADD: else if (!isSubAdminSelected)
      setPincodes([]);
    }
  }, [city, pincodeList, state?.id, isSubAdminSelected]); // ADD: isSubAdminSelected to dependencies

  const handleUserTypeChange = (e) => setUserType(e.target.value);
  const handleStateChange = (e) => {
    setStates(e.target.value);
    setCity("");
    if (!isSubAdminSelected) {
      // ADD: Only reset if not using sub-admin
      setPincodes([]);
    }
  };
  const handleCountryChange = (e) => setCountry(e.target.value);
  const handleCityChange = (e) => {
    setCity(e.target.value);
    if (!isSubAdminSelected) {
      // ADD: Only reset if not using sub-admin
      setPincodes([]);
    }
    setErrorMessages((prev) => ({ ...prev, pincodes: "" }));
  };
  const handleBloodGroupChange = (e) => setBloodGroup(e.target.value);
  const handleAdminChange = (e) => {
    setSelectedAdmin(e.target.value);
    setSelectedSubAdmin("");
    setIsSubAdminSelected(false); // ADD THIS LINE
    setSubAdminPincodes([]); // ADD THIS LINE
    setPincodes([]); // ADD THIS LINE
    setErrorMessages((prev) => ({ ...prev, admin: "" }));
  };
  const handleSubAdminChange = (e) => {
    const subAdminId = e.target.value;
    setSelectedSubAdmin(subAdminId);
    setIsSubAdminSelected(!!subAdminId); // NEW: Set flag when sub-admin is selected

    if (subAdminId) {
      // Find the selected sub-admin's city
      const selectedSubAdminData = subAdminList.find(
        (sa) => sa.id === parseInt(subAdminId, 10)
      );

      if (selectedSubAdminData && selectedSubAdminData.city) {
        setCity(selectedSubAdminData.city);
        setErrorMessages((prev) => ({ ...prev, city: "" }));
      }

      setPincodes([]); // Clear pincodes, they'll be populated by the useEffect
    } else {
      setIsSubAdminSelected(false);
      setSubAdminPincodes([]);
      setPincodes([]);
    }

    setErrorMessages((prev) => ({ ...prev, subAdmin: "" }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
    if (errorMessages[name]) {
      setErrorMessages((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateField = (field, fieldName) => {
    if (!field || field === "") {
      setErrorMessages((prevState) => ({
        ...prevState,
        [fieldName]: `${fieldName} is a mandatory field.`,
      }));
      return true;
    }
    setErrorMessages((prevState) => ({ ...prevState, [fieldName]: "" }));
    return false;
  };

  const validateFileSize = (file) => {
    if (file && file.size > MAX_FILE_SIZE) {
      return false;
    }
    return true;
  };

  const compressImage = (file, maxSizeMB = 1) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;
          const maxWidth = 1920;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          let quality = 0.8;
          canvas.toBlob(
            (blob) => {
              if (blob.size > maxSizeMB * 1024 * 1024 && quality > 0.5) {
                quality -= 0.1;
                canvas.toBlob(
                  (newBlob) => {
                    resolve(
                      new File([newBlob], file.name, { type: "image/jpeg" })
                    );
                  },
                  "image/jpeg",
                  quality
                );
              } else {
                resolve(new File([blob], file.name, { type: "image/jpeg" }));
              }
            },
            "image/jpeg",
            quality
          );
        };
      };
      reader.onerror = reject;
    });
  };

  const handleFormSubmit = async () => {
    setErrorMessages({});
    setProfileErrorMessage("");
    setAddharErrorMessage("");

    let hasError = false;

    if (!profileImage || profileImage.length === 0) {
      setProfileErrorMessage("Please select a profile image to upload.");
      hasError = true;
    }

    if (!aadharImage || aadharImage.length === 0) {
      setAddharErrorMessage("Please select an Aadhar image to upload.");
      hasError = true;
    }

    if (pincodes.length === 0) {
      setErrorMessages((prev) => ({
        ...prev,
        pincodes: "Please select at least one pincode.",
      }));
      hasError = true;
    }

    const nameError = validateField(formValues.name, "name");
    const mobileError = validateField(formValues.mobile, "mobile");
    const emailError = validateField(formValues.email, "email");
    const guardianNameError = validateField(
      formValues.guardianName,
      "guardianName"
    );
    const guardianMobileError = validateField(
      formValues.guardianMobile,
      "guardianMobile"
    );
    const countryError = validateField(country, "country");
    const stateError = validateField(states, "state");
    const cityError = validateField(city, "city");
    const addressError = validateField(formValues.address, "address");
    const panNoError = validateField(formValues.panNo, "panNo");
    const dobError = validateField(formValues.dob, "dob");
    const userTypeError = validateField(userType, "userType");
    const bloodGroupError = validateField(bloodGroup, "bloodGroup");
    const adminError = validateField(selectedAdmin, "admin");
    const subAdminError = validateField(selectedSubAdmin, "subAdmin");

    if (
      nameError ||
      mobileError ||
      emailError ||
      guardianNameError ||
      guardianMobileError ||
      countryError ||
      stateError ||
      cityError ||
      addressError ||
      hasError ||
      panNoError ||
      dobError ||
      userTypeError ||
      bloodGroupError ||
      adminError ||
      subAdminError
    ) {
      showMessage("error", "Please fill all mandatory fields");
      return;
    }

    try {
      const formData = new FormData();

      if (profileImage && profileImage instanceof File) {
        const compressedProfile = await compressImage(profileImage, 1);
        formData.append("profileImage", compressedProfile);
      }

      if (aadharImage && aadharImage instanceof File) {
        const compressedAadhar = await compressImage(aadharImage, 1);
        formData.append("aadharImage", compressedAadhar);
      }

      formData.append("workType", userType);
      formData.append("bloodGroup", bloodGroup);
      formData.append("name", formValues.name);
      formData.append("mobile", formValues.mobile);
      formData.append("email", formValues.email);
      formData.append("guardianName", formValues.guardianName);
      formData.append("guardianMobile", formValues.guardianMobile);
      formData.append("country", country);
      formData.append("state", states);
      formData.append("city", city);
      formData.append("address", formValues.address);
      formData.append("pincode", JSON.stringify(pincodes));
      formData.append("panNo", formValues.panNo);
      formData.append("dob", formValues.dob);
      formData.append("adminId", selectedAdmin);
      formData.append("subAdminId", selectedSubAdmin);

      let response;

      if (state) {
        response = await updateEmployee({ formData, id: state.id });
      } else {
        response = await addEmployee(formData);
      }

      const { status, message } = response?.data || {};
      if (status) {
        navigate("/empolyees");
        showMessage("success", message);
      } else {
        // ✅ CHECK FOR BLOCKING ORDERS IN ERROR RESPONSE
        if (response?.data?.blockingOrders && response?.data?.blockingOrders.length > 0) {
          const firstBlocking = response.data.blockingOrders[0];
          setOrderDialog({
            open: true,
            pincode: firstBlocking.pincode,
            orders: firstBlocking.orders.map(orderNumber => ({ orderNumber })),
            totalOrders: firstBlocking.orders.length,
          });
        } else {
          showMessage("error", message || "Failed to save employee");
        }
      }
    } catch (error) {
      console.error("Failed to save the employee:", error);

      // ✅ HANDLE ERROR RESPONSE WITH ORDER DETAILS
      if (error?.data?.blockingOrders && error.data.blockingOrders.length > 0) {
        const firstBlocking = error.data.blockingOrders[0];
        setOrderDialog({
          open: true,
          pincode: firstBlocking.pincode,
          orders: firstBlocking.orders.map(orderNumber => ({ orderNumber })),
          totalOrders: firstBlocking.orders.length,
        });
      } else {
        showMessage("error", "An error occurred while saving the employee");
      }
    }
  };

  return (
    <>
      <Stack flexDirection={"row"} gap={1} mb={1}>
        <BackButton />
        <Heading head={state ? "Edit Employee" : "Add Employee"} />
      </Stack>
      <Box className="card_container">
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={12}>
            <Typography
              fontSize={{ xs: "12px", sm: "13px" }}
              fontWeight={500}
              mb={"2px"}
            >
              User Photo (Max 5MB)
            </Typography>
            <UploadImage files={profileImage} setFiles={setProfileImage} />
            <ErrorMessage message={profileErrorMessage} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Select
              selectList={isLoading ? [] : serviceList}
              labelinput="User Type"
              name="workType"
              value={userType}
              onChange={handleUserTypeChange}
            />
            {errorMessages.userType && (
              <ErrorMessage message={errorMessages.userType} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Select
              selectList={adminList}
              labelinput="Select Admin"
              name="admin"
              value={selectedAdmin}
              onChange={handleAdminChange}
              disabled={
                adminRole === 2 || adminRole === 3 || adminDropdownLoading
              }
            />
            {errorMessages.admin && (
              <ErrorMessage message={errorMessages.admin} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Select
              selectList={subAdminList}
              labelinput="Select Sub Admin"
              name="subAdmin"
              value={selectedSubAdmin}
              onChange={handleSubAdminChange}
              disabled={adminRole === 3 || !selectedAdmin || subAdminLoading}
            />
            {errorMessages.subAdmin && (
              <ErrorMessage message={errorMessages.subAdmin} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input
              labelinput="Technician Name"
              name="name"
              value={formValues.name}
              onChange={handleInputChange}
            />
            {errorMessages.name && (
              <ErrorMessage message={errorMessages.name} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input
              labelinput="Technician Mobile"
              type="number"
              name="mobile"
              value={formValues.mobile}
              onChange={handleInputChange}
            />
            {errorMessages.mobile && (
              <ErrorMessage message={errorMessages.mobile} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input
              labelinput="Email"
              type="email"
              name="email"
              value={formValues.email}
              onChange={handleInputChange}
            />
            {errorMessages.email && (
              <ErrorMessage message={errorMessages.email} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input
              labelinput="Guardian Name"
              name="guardianName"
              value={formValues.guardianName}
              onChange={handleInputChange}
            />
            {errorMessages.guardianName && (
              <ErrorMessage message={errorMessages.guardianName} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input
              labelinput="Guardian Mobile"
              name="guardianMobile"
              value={formValues.guardianMobile}
              onChange={handleInputChange}
            />
            {errorMessages.guardianMobile && (
              <ErrorMessage message={errorMessages.guardianMobile} />
            )}
          </Grid>
          <Grid item xs={12}>
            <Input
              labelinput="Address"
              name="address"
              value={formValues.address}
              onChange={handleInputChange}
            />
            {errorMessages.address && (
              <ErrorMessage message={errorMessages.address} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select
              selectList={countryList}
              labelinput="Country"
              name="country"
              value={country}
              onChange={handleCountryChange}
            />
            {errorMessages.country && (
              <ErrorMessage message={errorMessages.country} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select
              selectList={stateLoading ? [] : stateList}
              disabled={adminRole !== 1}
              labelinput="State"
              name="state"
              value={states}
              onChange={handleStateChange}
            />
            {errorMessages.state && (
              <ErrorMessage message={errorMessages.state} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select
              selectList={cityLoading ? [] : cityList}
              disabled={adminRole !== 1 || isSubAdminSelected || !!state?.id} // ADD: || isSubAdminSelected
              labelinput="City"
              name="city"
              value={city}
              onChange={handleCityChange}
            />
            {errorMessages.city && (
              <ErrorMessage message={errorMessages.city} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Input
              labelinput="Pan Card No"
              name="panNo"
              value={formValues.panNo}
              onChange={handleInputChange}
            />
            {errorMessages.panNo && (
              <ErrorMessage message={errorMessages.panNo} />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              fontSize={{ xs: "12px", sm: "13px" }}
              fontWeight={500}
              mb={"2px"}
            >
              Select Pincodes
            </Typography>
            <Autocomplete
              multiple
              id="pincode-select"
              options={pincodeList}
              loading={
                isSubAdminSelected
                  ? subAdminPincodesLoading
                  : extraPincodeLoading || cityPincodeLoading
              }
              disableCloseOnSelect
              getOptionLabel={(option) => {
                if (typeof option === "object") return option.pincode || "";
                return option || "";
              }}
              isOptionEqualToValue={(option, value) => {
                const optionValue =
                  typeof option === "object" ? option.pincode : option;
                const valueValue =
                  typeof value === "object" ? value.pincode : value;
                return optionValue === valueValue;
              }}
              value={pincodeList.filter((p) => pincodes.includes(p.pincode))}
              onChange={(event, newValue) => {
                const formattedPincodes = newValue.map((item) =>
                  typeof item === "object" ? item.pincode : item
                );
                setPincodes(formattedPincodes);
                setErrorMessages((prev) => ({ ...prev, pincodes: "" }));
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                const pincodeValue =
                  typeof option === "object" ? option.pincode : option;
                return (
                  <li key={key} {...otherProps}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {pincodeValue}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select pincodes" />
              )}
            />
            {errorMessages.pincodes && (
              <ErrorMessage message={errorMessages.pincodes} />
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography
              fontSize={{ xs: "12px", sm: "13px" }}
              fontWeight={500}
              mb={"2px"}
            >
              Upload Aadhar Card (Max 5MB)
            </Typography>
            <UploadImage files={aadharImage} setFiles={setAadharImage} />
            <ErrorMessage message={addharErrorMessage} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Select
              selectList={bloodGroupList}
              labelinput="Blood Group"
              name="bloodGroup"
              value={bloodGroup}
              onChange={handleBloodGroupChange}
            />
            {errorMessages.bloodGroup && (
              <ErrorMessage message={errorMessages.bloodGroup} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input
              labelinput="User DOB"
              name="dob"
              type="date"
              value={formValues.dob}
              onChange={handleInputChange}
            />
            {errorMessages.dob && <ErrorMessage message={errorMessages.dob} />}
          </Grid>
          <Grid item xs={12}>
            <Stack flexDirection={"row"} gap={1}>
              <Button buttonName="save" onClick={handleFormSubmit} />
              <Button
                buttonName="cancel"
                color="white"
                onClick={() => navigate("/empolyees")}
              />
            </Stack>
          </Grid>
        </Grid>
      </Box>
      {/* ✅ ORDER ASSOCIATION WARNING DIALOG */}
      <Dialog
        open={orderDialog.open}
        onClose={handleCloseOrderDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Cannot Remove Pincode
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Pincode <strong>{orderDialog.pincode}</strong> is associated with{' '}
            <strong>{orderDialog.totalOrders}</strong> active order(s) and cannot be removed from this employee.
          </DialogContentText>

          {orderDialog.orders && orderDialog.orders.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Associated Orders:
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order Number</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderDialog.orders.map((order, index) => (
                      <TableRow key={index}>
                        <TableCell>{order.orderNumber}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <DialogContentText sx={{ mt: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
            You can only remove pincodes from employees if the associated orders have status:
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Upcoming/Placed</li>
              <li>Assigned to Technician</li>
              <li>Completed</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button buttonName="Close" onClick={handleCloseOrderDialog} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddEmployees;
